import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, User, Briefcase, Calendar, Globe, Utensils,
  Sparkles, CheckCircle, Loader2, Send, MapPin,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONFERENCE = {
  name: '4th Annual Citywide Conference',
  theme: 'Brotherhood, Belonging, and Brilliance: Redefining Men in ECE',
  date: 'Monday, October 12, 2026',
  location: 'Aspen Institute | Washington, DC',
};

const STEPS = [
  { number: 1, title: 'Contact', icon: User },
  { number: 2, title: 'Professional', icon: Briefcase },
  { number: 3, title: 'Experience', icon: Calendar },
  { number: 4, title: 'Language', icon: Globe },
  { number: 5, title: 'Access & Food', icon: Utensils },
  { number: 6, title: 'Engagement', icon: Sparkles },
];

const ROLES = [
  'Teacher', 'Assistant Teacher', 'Family Child Care Provider', 'Center Director',
  'Program Administrator', 'Higher Education Faculty', 'Researcher', 'Student',
  'Coach/Mentor', 'Advocate', 'Policymaker', 'Community Partner',
];
const YEARS_EXPERIENCE = ['Less than 1 year', '1–3 years', '4–7 years', '8–15 years', '16+ years'];
const PREVIOUS_YEARS = ['2023', '2024', '2025'];
const HOW_HEARD = [
  'Social Media', 'Colleague/Friend', 'Employer', 'DC Men in ECE Website',
  'Email Campaign', 'Previous Attendee',
];
const LANGUAGES = ['English', 'Spanish', 'Amharic', 'French', 'Arabic', 'Mandarin'];
const RACE_ETHNICITY = [
  'Black/African American', 'Hispanic/Latino/a/x', 'White', 'Asian',
  'Native American/Alaska Native', 'Native Hawaiian/Pacific Islander',
  'Multiracial', 'Prefer Not to Answer',
];
const DIETARY = [
  'None', 'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free',
  'Nut Allergy', 'Shellfish Allergy', 'Kosher', 'Halal',
];
const LOOKING_FORWARD = [
  'Keynote Speaker', 'Professional Development Workshops', 'Leadership Development',
  'Networking', 'Research & Best Practices', 'Advocacy & Policy Discussions',
  'Career Growth Opportunities', 'Brotherhood & Community Building',
];

const YES = 'Yes';
const NO = 'No';

const initialForm = {
  first_name: '', last_name: '', preferred_name: '', email: '', mobile_phone: '',
  organization: '', preferred_organization: '', job_title: '', city: '', state: '',
  roles: [], roles_other: '', years_experience: '',
  attended_before: '', previous_years: [], previous_years_other: '',
  how_heard: '', how_heard_other: '',
  first_language: '', first_language_other: '',
  race_ethnicity: [], race_ethnicity_other: '',
  needs_accommodations: '', accommodations_description: '',
  dietary_restrictions: [], dietary_other: '',
  looking_forward_to: [],
  future_events_info: '', first_ece_conference: '', mailing_list_opt_in: '', sponsorship_info: '',
  media_consent: '', agreement: false,
};

function toggleInArray(arr, value) {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/* ----------------------- Small primitive helpers ----------------------- */

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-8">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center shadow-lg shadow-gold-500/25">
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <h2 className="text-2xl font-bold text-navy-900">{title}</h2>
      {subtitle && <p className="text-sm text-navy-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const Field = ({ label, htmlFor, required, hint, children }) => (
  <div>
    <Label htmlFor={htmlFor} className="text-sm font-medium text-navy-800">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-xs text-navy-500 mt-1.5">{hint}</p>}
  </div>
);

const RadioGroup = ({ name, value, onChange, options, testIdPrefix }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {options.map((opt) => {
      const optValue = typeof opt === 'string' ? opt : opt.value;
      const optLabel = typeof opt === 'string' ? opt : opt.label;
      const selected = value === optValue;
      return (
        <label
          key={optValue}
          className={`cursor-pointer rounded-xl border-2 p-4 flex items-start gap-3 transition-all ${
            selected
              ? 'border-brand-gold bg-gold-50 shadow-sm'
              : 'border-navy-100 bg-white hover:border-navy-200 hover:shadow-sm'
          }`}
          data-testid={`${testIdPrefix}-${optValue.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <input
            type="radio"
            name={name}
            value={optValue}
            checked={selected}
            onChange={() => onChange(optValue)}
            className="mt-1 accent-brand-gold"
          />
          <span className="text-sm font-medium text-navy-800 leading-snug">{optLabel}</span>
        </label>
      );
    })}
  </div>
);

const CheckboxGroup = ({ values, onChange, options, testIdPrefix, columns = 2 }) => {
  const gridClass = columns === 3 ? 'sm:grid-cols-3' : columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1';
  return (
    <div className={`grid ${gridClass} gap-2`}>
      {options.map((opt) => {
        const checked = values.includes(opt);
        return (
          <label
            key={opt}
            className={`cursor-pointer rounded-lg border-2 px-4 py-3 flex items-center gap-3 transition-all ${
              checked
                ? 'border-brand-green bg-green-50 shadow-sm'
                : 'border-navy-100 bg-white hover:border-navy-200'
            }`}
            data-testid={`${testIdPrefix}-${opt.replace(/[\s/&]+/g, '-').toLowerCase()}`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => onChange(opt)}
              className="data-[state=checked]:bg-brand-green data-[state=checked]:border-brand-green"
            />
            <span className="text-sm text-navy-800 leading-snug">{opt}</span>
          </label>
        );
      })}
    </div>
  );
};

/* --------------------------------- Page --------------------------------- */

export default function ConferenceRegisterPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleField = (key, value) => setForm((f) => ({ ...f, [key]: toggleInArray(f[key], value) }));

  const validateStep = (step) => {
    if (step === 1) {
      if (!form.first_name.trim()) return 'First name is required';
      if (!form.last_name.trim()) return 'Last name is required';
      if (!form.email.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email';
    }
    if (step === 2) {
      if (form.roles.length === 0 && !form.roles_other.trim()) return 'Please select at least one role';
      if (!form.years_experience) return 'Please select your years of experience';
    }
    if (step === 3) {
      if (!form.attended_before) return 'Please answer whether you have attended before';
      if (!form.how_heard && !form.how_heard_other.trim()) return 'Please tell us how you heard about the conference';
    }
    if (step === 4) {
      if (!form.first_language && !form.first_language_other.trim()) return 'Please select your first language';
    }
    if (step === 5) {
      if (!form.needs_accommodations) return 'Please answer the accessibility question';
      if (form.needs_accommodations === YES && !form.accommodations_description.trim()) {
        return 'Please describe your accommodation needs';
      }
      if (form.dietary_restrictions.length === 0 && !form.dietary_other.trim()) {
        return 'Please select at least one dietary option (choose "None" if not applicable)';
      }
    }
    if (step === 6) {
      if (form.looking_forward_to.length === 0) return 'Please pick at least one thing you are looking forward to';
      if (form.looking_forward_to.length > 3) return 'Please pick up to 3';
      if (!form.future_events_info) return 'Please answer the future events question';
      if (!form.first_ece_conference) return 'Please answer whether this is your first ECE conference';
      if (!form.mailing_list_opt_in) return 'Please answer the mailing list question';
      if (!form.sponsorship_info) return 'Please answer the sponsorship question';
      if (!form.media_consent) return 'Please make a media consent selection';
      if (!form.agreement) return 'You must agree to the registration terms';
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) return toast.error(err);
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep(6);
    if (err) return toast.error(err);

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/conference/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Registration failed');
      setSuccessData({
        first_name: form.first_name,
        email: form.email,
        confirmation_email_sent: data.confirmation_email_sent,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------- Success Screen --------------------------- */
  if (successData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl overflow-hidden" data-testid="conference-reg-success">
            <div className="bg-gradient-to-br from-brand-navy to-navy-800 p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-brand-green mx-auto flex items-center justify-center mb-4 shadow-lg">
                <CheckCircle className="w-9 h-9 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">You&apos;re Registered!</h1>
              <p className="text-navy-200 text-base">See you October 12, 2026</p>
            </div>
            <CardContent className="p-8 space-y-4 text-center">
              <p className="text-navy-800 text-lg">
                Thank you, <strong>{successData.first_name}</strong>. Your spot at the{' '}
                <strong>{CONFERENCE.name}</strong> is confirmed.
              </p>
              {successData.confirmation_email_sent ? (
                <p className="text-navy-600 text-sm">
                  A confirmation email has been sent to <strong>{successData.email}</strong>.
                </p>
              ) : (
                <p className="text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Your registration is saved. The confirmation email couldn&apos;t be sent right now, but we&apos;ll follow up shortly.
                </p>
              )}
              <div className="rounded-xl bg-navy-50 p-5 text-left border border-navy-100">
                <p className="text-xs uppercase tracking-wider text-navy-500 font-semibold mb-1">Theme</p>
                <p className="text-navy-900 font-bold mb-3">{CONFERENCE.theme}</p>
                <div className="flex items-start gap-2 text-navy-700 text-sm mb-1">
                  <Calendar className="w-4 h-4 mt-0.5 text-brand-gold" /> {CONFERENCE.date}
                </div>
                <div className="flex items-start gap-2 text-navy-700 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 text-brand-gold" /> {CONFERENCE.location}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Button
                  onClick={() => navigate('/conference')}
                  className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold"
                  data-testid="reg-success-back-to-conference"
                >
                  Back to Conference
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                >
                  Go Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  /* --------------------------- Registration Form --------------------------- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
      {/* Hero */}
      <section className="relative py-16 bg-gradient-to-br from-brand-navy to-navy-800">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(201,161,59,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(34,197,94,0.12), transparent 40%)'
        }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <nav className="flex items-center justify-center gap-2 text-sm text-navy-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/conference" className="hover:text-white transition-colors">Conference</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Register</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6">
            <Calendar className="w-4 h-4 text-brand-gold" />
            <span className="text-sm font-medium text-brand-gold">October 12, 2026 · Aspen Institute, DC</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Register for the <span className="text-brand-gold">{CONFERENCE.name}</span>
          </h1>
          <p className="text-lg text-navy-200 max-w-2xl mx-auto italic">
            {CONFERENCE.theme}
          </p>
          <p className="text-sm text-navy-300 mt-4">
            Registration is free. Please complete all six sections below.
          </p>
        </div>
      </section>

      {/* Progress Stepper */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-navy-100">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted
                          ? 'bg-brand-green text-white'
                          : isActive
                          ? 'bg-brand-gold text-navy-900 shadow-lg shadow-gold-500/30 scale-110'
                          : 'bg-navy-100 text-navy-400'
                      }`}
                    >
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`mt-2 text-xs font-medium hidden sm:block ${
                      isActive ? 'text-brand-gold' : isCompleted ? 'text-brand-green' : 'text-navy-400'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-1 sm:mx-2 rounded-full transition-all duration-300 ${
                      currentStep > step.number ? 'bg-brand-green' : 'bg-navy-100'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <form onSubmit={handleSubmit} data-testid="conference-registration-form">

          {/* STEP 1 - Contact Info */}
          {currentStep === 1 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-6">
                <SectionHeader icon={User} title="Contact Information" subtitle="Tell us who you are and how to reach you." />

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="First Name" htmlFor="first_name" required>
                    <Input id="first_name" data-testid="reg-first-name"
                      value={form.first_name} onChange={(e) => update('first_name', e.target.value)} />
                  </Field>
                  <Field label="Last Name" htmlFor="last_name" required>
                    <Input id="last_name" data-testid="reg-last-name"
                      value={form.last_name} onChange={(e) => update('last_name', e.target.value)} />
                  </Field>
                </div>

                <Field label="Preferred Name / Badge Name" htmlFor="preferred_name"
                  hint="Leave blank if same as first name.">
                  <Input id="preferred_name" data-testid="reg-preferred-name"
                    value={form.preferred_name} onChange={(e) => update('preferred_name', e.target.value)} />
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email Address" htmlFor="email" required>
                    <Input id="email" type="email" data-testid="reg-email"
                      value={form.email} onChange={(e) => update('email', e.target.value)} />
                  </Field>
                  <Field label="Mobile Phone Number" htmlFor="mobile_phone">
                    <Input id="mobile_phone" type="tel" data-testid="reg-phone"
                      value={form.mobile_phone} onChange={(e) => update('mobile_phone', e.target.value)} />
                  </Field>
                </div>

                <Field label="Organization / Employer" htmlFor="organization">
                  <Input id="organization" data-testid="reg-org"
                    value={form.organization} onChange={(e) => update('organization', e.target.value)} />
                </Field>

                <Field label="Preferred Organization / Employer for Badge" htmlFor="preferred_organization"
                  hint="Leave blank to use the same as above.">
                  <Input id="preferred_organization" data-testid="reg-preferred-org"
                    value={form.preferred_organization} onChange={(e) => update('preferred_organization', e.target.value)} />
                </Field>

                <Field label="Job Title / Role" htmlFor="job_title">
                  <Input id="job_title" data-testid="reg-job-title"
                    value={form.job_title} onChange={(e) => update('job_title', e.target.value)} />
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="City" htmlFor="city">
                    <Input id="city" data-testid="reg-city" value={form.city} onChange={(e) => update('city', e.target.value)} />
                  </Field>
                  <Field label="State" htmlFor="state">
                    <Input id="state" data-testid="reg-state" value={form.state} onChange={(e) => update('state', e.target.value)} />
                  </Field>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 2 - Professional */}
          {currentStep === 2 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-8">
                <SectionHeader icon={Briefcase} title="Professional Information" subtitle="Help us understand who's in the room." />

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Which best describes your role? <span className="text-red-500">*</span>
                    <span className="ml-1 text-xs font-normal text-navy-500">Select all that apply</span>
                  </Label>
                  <div className="mt-3">
                    <CheckboxGroup
                      values={form.roles}
                      onChange={(v) => toggleField('roles', v)}
                      options={ROLES}
                      testIdPrefix="reg-role"
                      columns={2}
                    />
                    <Input
                      className="mt-3"
                      placeholder="Other role (optional)"
                      value={form.roles_other}
                      onChange={(e) => update('roles_other', e.target.value)}
                      data-testid="reg-roles-other"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Years of experience in Early Childhood Education <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-3">
                    <RadioGroup
                      name="years_experience"
                      value={form.years_experience}
                      onChange={(v) => update('years_experience', v)}
                      options={YEARS_EXPERIENCE}
                      testIdPrefix="reg-years"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 3 - Conference Experience */}
          {currentStep === 3 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-8">
                <SectionHeader icon={Calendar} title="Conference Experience" subtitle="Have we met before?" />

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Have you attended a DC Men in ECE Conference before? <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-3">
                    <RadioGroup
                      name="attended_before"
                      value={form.attended_before}
                      onChange={(v) => update('attended_before', v)}
                      options={[YES, NO]}
                      testIdPrefix="reg-attended-before"
                    />
                  </div>
                </div>

                {form.attended_before === YES && (
                  <div>
                    <Label className="text-sm font-medium text-navy-800">
                      Which conference(s)? <span className="text-xs font-normal text-navy-500">Select all that apply</span>
                    </Label>
                    <div className="mt-3">
                      <CheckboxGroup
                        values={form.previous_years}
                        onChange={(v) => toggleField('previous_years', v)}
                        options={PREVIOUS_YEARS}
                        testIdPrefix="reg-prev-year"
                        columns={3}
                      />
                      <Input
                        className="mt-3"
                        placeholder="Other year (optional)"
                        value={form.previous_years_other}
                        onChange={(e) => update('previous_years_other', e.target.value)}
                        data-testid="reg-prev-year-other"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    How did you hear about this conference? <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-3">
                    <RadioGroup
                      name="how_heard"
                      value={form.how_heard}
                      onChange={(v) => update('how_heard', v)}
                      options={HOW_HEARD}
                      testIdPrefix="reg-how-heard"
                    />
                    <Input
                      className="mt-3"
                      placeholder="Other (please describe)"
                      value={form.how_heard_other}
                      onChange={(e) => update('how_heard_other', e.target.value)}
                      data-testid="reg-how-heard-other"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 4 - Language & Demographics */}
          {currentStep === 4 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-8">
                <SectionHeader icon={Globe} title="Language & Demographics" subtitle="Helps us plan for accessibility and inclusion." />

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    What is your first language? <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-3">
                    <RadioGroup
                      name="first_language"
                      value={form.first_language}
                      onChange={(v) => update('first_language', v)}
                      options={LANGUAGES}
                      testIdPrefix="reg-lang"
                    />
                    <Input
                      className="mt-3"
                      placeholder="Other language (please describe)"
                      value={form.first_language_other}
                      onChange={(e) => update('first_language_other', e.target.value)}
                      data-testid="reg-lang-other"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Race / Ethnicity <span className="text-xs font-normal text-navy-500">(Optional · select all that apply)</span>
                  </Label>
                  <div className="mt-3">
                    <CheckboxGroup
                      values={form.race_ethnicity}
                      onChange={(v) => toggleField('race_ethnicity', v)}
                      options={RACE_ETHNICITY}
                      testIdPrefix="reg-race"
                      columns={2}
                    />
                    <Input
                      className="mt-3"
                      placeholder="Other (optional)"
                      value={form.race_ethnicity_other}
                      onChange={(e) => update('race_ethnicity_other', e.target.value)}
                      data-testid="reg-race-other"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 5 - Accessibility & Dietary */}
          {currentStep === 5 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-8">
                <SectionHeader icon={Utensils} title="Accessibility & Dietary Needs" subtitle="So we can make sure you're set." />

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Do you require any accommodations to fully participate? <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-3">
                    <RadioGroup
                      name="needs_accommodations"
                      value={form.needs_accommodations}
                      onChange={(v) => update('needs_accommodations', v)}
                      options={[NO, YES]}
                      testIdPrefix="reg-needs-accom"
                    />
                  </div>
                  {form.needs_accommodations === YES && (
                    <div className="mt-4">
                      <Label htmlFor="accom_desc" className="text-sm text-navy-700">
                        Please describe your accommodation needs <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="accom_desc"
                        rows={3}
                        placeholder="E.g., mobility, ASL interpretation, captioning, lactation space, visual or hearing assistance..."
                        value={form.accommodations_description}
                        onChange={(e) => update('accommodations_description', e.target.value)}
                        className="mt-1.5"
                        data-testid="reg-accom-desc"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Any dietary restrictions or food allergies? <span className="text-red-500">*</span>
                    <span className="ml-1 text-xs font-normal text-navy-500">Select all that apply</span>
                  </Label>
                  <div className="mt-3">
                    <CheckboxGroup
                      values={form.dietary_restrictions}
                      onChange={(v) => toggleField('dietary_restrictions', v)}
                      options={DIETARY}
                      testIdPrefix="reg-diet"
                      columns={2}
                    />
                    <Input
                      className="mt-3"
                      placeholder="Other dietary need (optional)"
                      value={form.dietary_other}
                      onChange={(e) => update('dietary_other', e.target.value)}
                      data-testid="reg-diet-other"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 6 - Networking & Consent */}
          {currentStep === 6 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-8">
                <SectionHeader icon={Sparkles} title="Engagement & Consent" subtitle="Last step — you're almost there." />

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    What are you most looking forward to? <span className="text-red-500">*</span>
                    <span className="ml-1 text-xs font-normal text-navy-500">Select up to 3 ({form.looking_forward_to.length}/3)</span>
                  </Label>
                  <div className="mt-3">
                    <CheckboxGroup
                      values={form.looking_forward_to}
                      onChange={(v) => {
                        const already = form.looking_forward_to.includes(v);
                        if (!already && form.looking_forward_to.length >= 3) {
                          toast.error('You can select up to 3');
                          return;
                        }
                        toggleField('looking_forward_to', v);
                      }}
                      options={LOOKING_FORWARD}
                      testIdPrefix="reg-forward"
                      columns={2}
                    />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm font-medium text-navy-800 block">Info on future events? <span className="text-red-500">*</span></Label>
                    <div className="mt-3">
                      <RadioGroup name="future_events_info" value={form.future_events_info}
                        onChange={(v) => update('future_events_info', v)} options={[YES, NO]}
                        testIdPrefix="reg-future" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-navy-800 block">First ECE conference? <span className="text-red-500">*</span></Label>
                    <div className="mt-3">
                      <RadioGroup name="first_ece_conference" value={form.first_ece_conference}
                        onChange={(v) => update('first_ece_conference', v)} options={[YES, NO]}
                        testIdPrefix="reg-first-ece" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-navy-800 block">Join mailing list? <span className="text-red-500">*</span></Label>
                    <div className="mt-3">
                      <RadioGroup name="mailing_list_opt_in" value={form.mailing_list_opt_in}
                        onChange={(v) => update('mailing_list_opt_in', v)} options={[YES, NO]}
                        testIdPrefix="reg-mailing" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-navy-800 block">Sponsorship / partnership info? <span className="text-red-500">*</span></Label>
                    <div className="mt-3">
                      <RadioGroup name="sponsorship_info" value={form.sponsorship_info}
                        onChange={(v) => update('sponsorship_info', v)} options={[YES, NO]}
                        testIdPrefix="reg-sponsor" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-navy-800">
                    Media Release <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-navy-500 mt-1 mb-3">Photos and videos may be taken during the conference for promotional and educational purposes.</p>
                  <RadioGroup
                    name="media_consent"
                    value={form.media_consent}
                    onChange={(v) => update('media_consent', v)}
                    options={[
                      { value: 'consent', label: 'I consent to being photographed and/or recorded.' },
                      { value: 'no_consent', label: 'I do not consent to being photographed and/or recorded.' },
                    ]}
                    testIdPrefix="reg-media"
                  />
                </div>

                <div className="border-t border-navy-100 pt-6">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="reg-agreement">
                    <Checkbox
                      checked={form.agreement}
                      onCheckedChange={(v) => update('agreement', Boolean(v))}
                      className="mt-1 data-[state=checked]:bg-brand-green data-[state=checked]:border-brand-green"
                    />
                    <span className="text-sm text-navy-700 leading-relaxed">
                      By submitting this registration, I understand that conference details may be updated and communicated via email. I agree to receive conference-related communications regarding logistics, program updates, and event information. <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Nav Buttons */}
          <div className="flex items-center justify-between mt-8">
            <Button
              type="button"
              onClick={goBack}
              disabled={currentStep === 1}
              variant="outline"
              className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50 disabled:opacity-40"
              data-testid="reg-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>

            <div className="text-sm text-navy-500">Step {currentStep} of {STEPS.length}</div>

            {currentStep < STEPS.length ? (
              <Button
                type="button"
                onClick={goNext}
                className="bg-brand-navy hover:bg-navy-700 text-white font-semibold px-6"
                data-testid="reg-next-btn"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={submitting}
                className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-bold px-8 shadow-lg shadow-gold-500/30"
                data-testid="reg-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Complete Registration</>
                )}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
